package org.teddapp.project.ui.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerSession
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTagTone
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddPasswordTransformation

/** Ce que l'application dit d'un refus du serveur, en clair. */
internal fun serverMessage(e: ServerException): String = when (e.code) {
    "invalid_credentials" -> "E-mail ou mot de passe incorrect."
    "too_many_attempts" -> "Trop d'essais. Réessaie dans un quart d'heure."
    "forbidden" -> "Cette action est réservée au supra_user."
    "admin_unavailable" -> "La gestion des comptes n'est pas configurée sur ce serveur."
    "email_taken" -> "Un compte existe déjà pour cette adresse."
    "session_revoked", "device_revoked", "not_connected" -> "La session a été fermée. Reconnecte-toi au serveur."
    else -> e.message ?: "Le serveur a refusé."
}

/** Réglages : la connexion de cet appareil au serveur teddApp. */
@Composable
internal fun ServerSection(session: ServerSession) {
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    Column(modifier = Modifier.padding(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        if (session.connected) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                TeddIcon(name = "check", size = 18.dp, color = TeddTokens.Success, strokeWidth = 2.dp)
                Column(modifier = Modifier.weight(1f)) {
                    Text(session.email.ifBlank { "Connecté" }, color = TeddTokens.Ink, style = TeddType.Label)
                    Text(session.url, color = TeddTokens.InkMuted, style = TeddType.MonoSm)
                }
                if (session.isSupra) TeddTag("supra_user", tone = TeddTagTone.Public, icon = "globe")
            }
            if (session.isSupra) {
                Text("Tu tiens le back-office du site : il est dans la barre latérale.", color = TeddTokens.InkMuted, style = TeddType.Caption)
            }
            TeddButton(
                text = if (busy) "Déconnexion…" else "Se déconnecter du serveur",
                onClick = { busy = true; scope.launch { session.signOut(); busy = false } },
                kind = TeddButtonKind.Secondary,
                enabled = !busy,
            )
        } else {
            var url by remember { mutableStateOf(session.url) }
            var email by remember { mutableStateOf(session.email) }
            var password by remember { mutableStateOf("") }
            // Sans compte serveur, rien ne quitte l'appareil : on le dit, et on propose de le créer.
            Row(verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Laterite)
                Text(
                    "Cet appareil n'est pas relié au serveur : tes données restent ici. Crée ton compte, ou connecte celui que tu as déjà, pour les retrouver sur ton téléphone et ton ordinateur.",
                    color = TeddTokens.InkMuted, style = TeddType.Caption,
                )
            }
            // L'adresse est imposée (production, ou TEDD_SERVER_URL en développement) : rien à saisir.
            if (!session.urlFixed) {
                TeddField(label = "Adresse du serveur", value = url, onValueChange = { url = it; error = null }, placeholder = "https://tedd.exemple.org")
            }
            TeddField(label = "E-mail", value = email, onValueChange = { email = it; error = null }, placeholder = "toi@exemple.org")
            TeddField(
                label = "Mot de passe",
                value = password,
                onValueChange = { password = it; error = null },
                visualTransformation = teddPasswordTransformation(),
            )
            error?.let { message ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Danger)
                    Text(message, color = TeddTokens.Danger, style = TeddType.Caption)
                }
            }
            TeddButton(
                text = if (busy) "Connexion…" else "Se connecter",
                onClick = {
                    busy = true
                    error = null
                    scope.launch {
                        try {
                            session.signIn(url, email, password)
                            password = ""
                        } catch (e: ServerException) {
                            error = serverMessage(e)
                        } finally {
                            busy = false
                        }
                    }
                },
                kind = TeddButtonKind.Primary,
                enabled = !busy && url.isNotBlank() && email.isNotBlank() && password.isNotEmpty(),
            )
            TeddButton(
                text = if (busy) "Création…" else "Créer mon compte sur le serveur",
                onClick = {
                    busy = true
                    error = null
                    scope.launch {
                        try {
                            session.signUp(url, email, password)
                            password = ""
                        } catch (e: ServerException) {
                            error = if (e.code == "signup_closed") "Les inscriptions sont fermées : demande ton compte à l'administrateur du serveur, puis connecte-toi." else serverMessage(e)
                        } finally {
                            busy = false
                        }
                    }
                },
                kind = TeddButtonKind.Secondary,
                enabled = !busy && url.isNotBlank() && email.isNotBlank() && password.isNotEmpty(),
            )
        }
    }
}
